import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { KashSurveyComponent } from "../../components/kash-survey.component";

/**
 * Assessment Template Preview Screen
 *
 * This screen displays the assessment template in readonly mode.
 * The template is loaded from session storage (passed from React Native WebView).
 *
 * Features:
 * - Readonly mode only (no submission)
 * - Template from session storage (automatically handles chunked/direct loading)
 * - Simple navigation
 * - Preview only, no data persistence
 */
export const AssessmentPreviewScreen: React.FC = () => {
  const { t } = useTranslation();
  const [surveyTemplate, setSurveyTemplate] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get language from session storage (passed from React Native)
  const appLanguage = window.sessionStorage.getItem("APP_LANGUAGE") || "en";

  /**
   * Map React Native app language code to Web i18n language code
   * App uses: zh_HK, zh_CN, en, etc.
   * Web uses: zhhk, zh-Hant, en, etc.
   */
  const mapAppLanguageToWebLanguage = (appLang: string): string => {
    const langMapping: Record<string, string> = {
      zh_HK: "zhhk",
      "zh-HK": "zhhk",
      zh_CN: "zh-cn",
      "zh-CN": "zh-cn",
      en: "en",
      id: "id",
      my: "my",
      th: "th",
      vi: "vi",
      km_KH: "km",
      "km-KH": "km",
    };
    return langMapping[appLang] || appLang.toLowerCase();
  };

  /**
   * Set global language for the entire web app
   */
  useEffect(() => {
    if (appLanguage) {
      const webLanguage = mapAppLanguageToWebLanguage(appLanguage);
      if (i18n.language !== webLanguage) {
        i18n.changeLanguage(webLanguage);
      }
    }
  }, [appLanguage]);

  /**
   * Load assessment template from session storage
   * Supports both direct injection and chunked transfer automatically
   */
  useEffect(() => {
    let mounted = true;

    const loadTemplate = () => {
      try {
        const templateJson = window.sessionStorage.getItem("SURVEY_TEMPLATE");

        if (!templateJson) {
          if (mounted) {
            setError(t("Incentive.errors.assessment_template_not_found"));
            setLoading(false);
          }
          return;
        }

        const template = JSON.parse(templateJson);
        if (mounted) {
          setSurveyTemplate(template);
          setLoading(false);
          setError(null);
          console.log("Assessment template loaded successfully");
        }
      } catch (err) {
        console.error("Failed to load assessment template:", err);
        if (mounted) {
          setError(
            err instanceof Error ? err.message : t("Incentive.errors.failed_to_parse_template"),
          );
          setLoading(false);
        }
      }
    };

    // Try immediate load (for direct injection)
    loadTemplate();

    // Listen for delayed load event (for chunked transfer)
    const handleTemplateReady = () => {
      console.log("Template ready event received");
      loadTemplate();
    };

    window.addEventListener("SURVEY_TEMPLATE_ready", handleTemplateReady);

    return () => {
      mounted = false;
      window.removeEventListener("SURVEY_TEMPLATE_ready", handleTemplateReady);
    };
  }, []);

  /**
   * Dummy submit handler (preview mode, no actual submission)
   */
  const handlePreviewSubmit = async (data: any): Promise<any> => {
    console.log("Preview mode - no submission:", data);
    return { success: true };
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {t("Incentive.loading_preview")}
        </h2>
        <p className="text-gray-600">{t("Incentive.loading_preview_message")}</p>
      </div>
    );
  }

  if (error || !surveyTemplate) {
    return (
      <div className="m-4">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            {t("Incentive.unable_to_load_preview")}
          </h2>
          <p className="text-gray-600 mb-6">{error || t("Incentive.template_not_available")}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* @ts-ignore - React type compatibility issue */}
      <KashSurveyComponent
        json={surveyTemplate}
        onSubmit={handlePreviewSubmit}
        disabled={true}
        language={appLanguage}
      />
    </div>
  );
};
